/* eslint-disable import/no-cycle */
/* eslint-disable no-param-reassign */
/* eslint-disable react-hooks/exhaustive-deps */

import React from 'react';
import isEqual from 'lodash/isEqual';
import isUndefined from 'lodash/isUndefined';
import { useStoreActions, useStoreState } from '../../../../models/hooks';
import { useSchemaNavigators } from '../useSchemaNavigators';
import { DropTarget, FormFieldT, FormItemT } from '../types';
import { DRAG_ITEM_TYPES } from '../constants';
import { checkIfSectionDraggedInsideSection, dragPreviewNode, getTargetIndicatorPath } from '../utils';

interface UseItemDragOverProps {
  path: number[];
  field: FormFieldT;
  itemRef: React.RefObject<HTMLDivElement>;
  onDropItem: (dropTarget: DropTarget) => void;
  parentIsAdvanced?: boolean;
}
export default function useFieldDragAndDrop({ path, field, itemRef, onDropItem, parentIsAdvanced }: UseItemDragOverProps) {
  const formSchema = useStoreState((state) => state.formBuilder.formSchema);
  const dropIndicator = useStoreState((state) => state.formBuilder.dropIndicator);
  const draggedItem = useStoreState((state) => state.formBuilder.draggedItem);
  const startDrag = useStoreActions((actions) => actions.formBuilder.startDrag);
  const endDrag = useStoreActions((actions) => actions.formBuilder.endDrag);
  const setDropIndicator = useStoreActions((actions) => actions.formBuilder.setDropIndicator);

  const { determineActualParentId } = useSchemaNavigators(formSchema);

  const handleDragStart = React.useCallback(
    (e: React.DragEvent) => {
      if (parentIsAdvanced) {
        return;
      }

      const itemToDrag: FormItemT = field;
      const payload = {
        id: field.id,
        type: DRAG_ITEM_TYPES.FORM_ITEM as 'formItem',
        itemData: { ...itemToDrag },
        sourcePath: path,
      };
      startDrag(payload);
      if (e.dataTransfer) {
        try {
          e.dataTransfer.setData('application/json', JSON.stringify(payload));
          e.dataTransfer.effectAllowed = 'move';
          if (itemRef.current) {
            dragPreviewNode(e, itemRef.current);
          }
        } catch (err) {
          console.error('Error setting dataTransfer in FormField:', err);
        }
      }
    },
    [startDrag, field.id, path, parentIsAdvanced],
  );

  const handleDragEnd = React.useCallback(
    (e: React.DragEvent) => {
      e.stopPropagation();
      if (draggedItem && draggedItem.type === DRAG_ITEM_TYPES.FORM_ITEM && (draggedItem.itemData as FormItemT).id === field.id) {
        if (e.dataTransfer && e.dataTransfer.dropEffect === 'none') {
          endDrag();
        }
      }
    },
    [draggedItem, endDrag, field.id],
  );

  const handleItemDragOver = React.useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();

      const setNoDropAndClearIndicator = () => {
        if (e.dataTransfer) {
          e.dataTransfer.dropEffect = 'none';
        }
        if (dropIndicator?.show || dropIndicator?.path) {
          //   dispatch({ type: 'SET_DROP_INDICATOR', payload: undefined });
          endDrag();
        }
        e.stopPropagation();
      };

      if (!draggedItem || !itemRef.current || parentIsAdvanced || (draggedItem.itemData as FormItemT).id === field.id) {
        // setNoDropAndClearIndicator();
        e.stopPropagation();
        return;
      }
      if (checkIfSectionDraggedInsideSection({ formSchema, draggedItem, path })) {
        setNoDropAndClearIndicator();
        return;
      }

      const { targetIndicatorPath } = getTargetIndicatorPath({ e, current: itemRef.current, path });

      const isCopy = draggedItem.type === DRAG_ITEM_TYPES.PALETTE;
      const dropEffect = isCopy ? 'copy' : 'move';

      if (dropIndicator?.show && dropIndicator?.path && isEqual(dropIndicator.path, targetIndicatorPath)) {
        if (e.dataTransfer) {
          e.dataTransfer.dropEffect = dropEffect;
        }
        e.stopPropagation();
        return;
      }

      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = dropEffect;
      }

      const parentContainerId = determineActualParentId(path);

      setDropIndicator({
        path: targetIndicatorPath,
        parentId: parentContainerId,
        isAdvancedSectionTarget: parentIsAdvanced,
        show: true,
      });
      e.stopPropagation();
    },
    [setDropIndicator, endDrag, determineActualParentId, path, field.id, draggedItem, itemRef, dropIndicator, parentIsAdvanced],
  );

  const handleItemDrop = React.useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (parentIsAdvanced || !draggedItem) {
        e.stopPropagation();
        return;
      }

      if (dropIndicator?.show && isUndefined(dropIndicator.parentId)) {
        e.stopPropagation();
        onDropItem(dropIndicator);
      }
    },
    [draggedItem, dropIndicator, onDropItem, parentIsAdvanced],
  );

  return { handleDragStart, handleDragEnd, handleItemDragOver, handleItemDrop };
}
